import { ArrowRight, RefreshCw, MessageSquare, UserPlus, Smartphone } from 'lucide-react';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  portalPageStyles,
  portalPricingPageStyles as styles,
} from '@/styles/portalPageStyles';

interface Tier {
  name: string;
  students: string;
  annual: string;
  setup: string;
  yearOne: string;
}

const tiers: readonly Tier[] = [
  { name: 'Micro', students: 'Up to 50', annual: '$750', setup: '$500', yearOne: '$1,250' },
  { name: 'Starter', students: '51\u2013100', annual: '$1,500', setup: '$750', yearOne: '$2,250' },
  { name: 'Small', students: '101\u2013300', annual: '$2,500', setup: '$1,500', yearOne: '$4,000' },
  { name: 'Medium', students: '301\u2013600', annual: '$3,500', setup: '$2,500', yearOne: '$6,000' },
  { name: 'Large', students: '601\u20131,200', annual: '$5,000', setup: '$4,000', yearOne: '$9,000' },
  { name: 'XL', students: '1,201\u20132,500', annual: '$6,500', setup: '$6,000', yearOne: '$12,500' },
  { name: 'Enterprise', students: '2,500+', annual: '$8,000', setup: '$9,000', yearOne: '$17,000' },
];

const rowLabels = [
  { key: 'students' as const, label: 'Students' },
  { key: 'annual' as const, label: 'Annual Fee' },
  { key: 'setup' as const, label: 'Setup (one-time)' },
  { key: 'yearOne' as const, label: 'Year 1 Total' },
];

const smsAddons = [
  {
    name: 'Base SMS',
    price: '$2/student/year',
    description: 'Text message notifications for behavior digests and targeted messages.',
  },
  {
    name: 'Schoolwide SMS',
    price: '$4/student/year',
    description: 'SMS delivery for school-wide and grade-level messages.',
    note: 'Requires Base SMS',
  },
];

function PricingPage() {
  usePageTitle('Pricing');
  const { isMobile } = useMediaQuery();

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav currentPage="pricing" />

      {/* Hero */}
      <section style={styles.hero}>
        <h1 style={isMobile ? styles.heroTitleMobile : styles.heroTitle}>
          Simple, Transparent Pricing
        </h1>
        <p style={isMobile ? styles.heroSubtitleMobile : styles.heroSubtitle}>
          One plan per school. No per-user fees. No surprises.
        </p>
      </section>

      <div style={portalPageStyles.container}>
        {/* Pricing Section */}
        <section style={portalPageStyles.section}>
          {!isMobile ? (
            /* Desktop Table */
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <caption style={styles.caption}>Pricing tiers by school size</caption>
                <thead>
                  <tr>
                    <th scope="col" style={{ ...styles.th, textAlign: 'left' as const }}>&nbsp;</th>
                    {tiers.map((tier) => (
                      <th key={tier.name} scope="col" style={styles.th}>{tier.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rowLabels.map((row, rowIndex) => (
                    <tr key={row.key} style={rowIndex % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <th scope="row" style={styles.thRowLabel}>{row.label}</th>
                      {tiers.map((tier) => (
                        <td key={tier.name} style={styles.td}>{tier[row.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* Mobile Cards */
            <div>
              {tiers.map((tier) => (
                <div key={tier.name} style={styles.mobileCard}>
                  <h3 style={styles.mobileCardTierName}>{tier.name}</h3>
                  <div style={styles.mobileCardStudents}>{tier.students} students</div>
                  <div style={styles.mobileCardRow}>
                    <span style={styles.mobileCardLabel}>Annual Fee</span>
                    <span style={styles.mobileCardValue}>{tier.annual}</span>
                  </div>
                  <div style={styles.mobileCardRow}>
                    <span style={styles.mobileCardLabel}>Setup (one-time)</span>
                    <span style={styles.mobileCardValue}>{tier.setup}</span>
                  </div>
                  <div style={{ ...styles.mobileCardRow, borderBottom: 'none' }}>
                    <span style={styles.mobileCardLabel}>Year 1 Total</span>
                    <span style={styles.mobileCardValue}>{tier.yearOne}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* SMS Add-Ons */}
        <section style={portalPageStyles.section}>
          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Smartphone size={22} style={{ color: '#f38d4b' }} />
              <h2 style={{ fontSize: '1.35rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                SMS Add-Ons
              </h2>
            </div>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: 0 }}>
              Optional per-student add-ons for text message delivery. Email notifications are always included.
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '1rem',
            maxWidth: '640px',
            margin: '0 auto',
          }}>
            {smsAddons.map((addon) => (
              <div key={addon.name} style={{
                backgroundColor: 'var(--surface-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                  {addon.name}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f38d4b', marginBottom: '0.75rem' }}>
                  {addon.price}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
                  {addon.description}
                </p>
                {'note' in addon && addon.note && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '0.5rem', marginBottom: 0, fontStyle: 'italic' }}>
                    {addon.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Info Boxes */}
        <section style={portalPageStyles.section}>
          <div style={isMobile ? styles.infoGridMobile : styles.infoGrid}>
            <div style={styles.infoBox}>
              <RefreshCw size={28} style={styles.infoBoxIcon} />
              <div style={styles.infoBoxTitle}>Year 2+ Renewal</div>
              <p style={styles.infoBoxDescription}>
                Annual fee only — no repeat setup cost. Your renewal is predictable and simple.
              </p>
            </div>
            <div style={styles.infoBox}>
              <MessageSquare size={28} style={styles.infoBoxIcon} />
              <div style={styles.infoBoxTitle}>SMS Notifications</div>
              <p style={styles.infoBoxDescription}>
                Per-student SMS add-ons for behavior notifications and school-wide messaging. Email notifications are always included at no extra cost.
              </p>
            </div>
            <div style={styles.infoBox}>
              <UserPlus size={28} style={styles.infoBoxIcon} />
              <div style={styles.infoBoxTitle}>New Hire Onboarding</div>
              <p style={styles.infoBoxDescription}>
                $75 per person for mid-year staff additions that need individual training.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={styles.cta}>
          <h2 style={styles.ctaTitle}>Ready to get started?</h2>
          <a href="/contact" style={styles.ctaButton}>
            Contact Sales
            <ArrowRight size={18} />
          </a>
          <p style={styles.ctaNote}>
            Free demo available for qualified schools &mdash;{' '}
            <a href="mailto:sales@checkademic.com" style={{ color: 'inherit' }}>
              sales@checkademic.com
            </a>
          </p>
        </section>
      </div>

      <PortalFooter currentPage="pricing" />
    </div>
  );
}

export default PricingPage;
